/**
 * SoreShop Creative Animation System (Production Ready)
 * Powered by GSAP, ScrollTrigger, Lenis, and SplitType
 */

// ─── 1. LENIS SMOOTH SCROLLER MODULE ─────────────────────────────────────────
class LenisScroll {
  constructor() {
    this.lenis = null;
  }

  init() {
    // Disabled to preserve 100% fast, native browser scrolling and instant anchor jumps
    return;
  }

  destroy() {
    if (this.lenis) {
      this.lenis.destroy();
    }
  }
}

// ─── 2. MAGNETIC HOVER MODULE ────────────────────────────────────────────────
class MagneticElement {
  constructor(element) {
    this.el = element;
    this.bindEvents();
  }

  bindEvents() {
    this.onMouseMove = (e) => {
      const rect = this.el.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * 0.35;
      const y = (e.clientY - rect.top - rect.height / 2) * 0.35;

      gsap.to(this.el, {
        x: x,
        y: y,
        duration: 0.3,
        ease: 'power2.out'
      });
    };

    this.onMouseLeave = () => {
      gsap.to(this.el, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: 'elastic.out(1.1, 0.45)'
      });
    };

    this.el.addEventListener('mousemove', this.onMouseMove);
    this.el.addEventListener('mouseleave', this.onMouseLeave);
  }

  destroy() {
    this.el.removeEventListener('mousemove', this.onMouseMove);
    this.el.removeEventListener('mouseleave', this.onMouseLeave);
    gsap.killTweensOf(this.el);
  }
}

// ─── 3. SPLITTYPE TEXT REVEAL MODULE ─────────────────────────────────────────
class SplitTextReveal {
  constructor(element) {
    this.el = element;
    this.split = null;
    this.init();
  }

  init() {
    if (typeof SplitType === 'undefined') return;

    // Split text into words and chars
    this.split = new SplitType(this.el, { types: 'words, chars' });

    // Set wrapping structure on words for mask reveal
    this.split.words.forEach(word => {
      word.style.display = 'inline-block';
      word.style.overflow = 'hidden';
      word.style.verticalAlign = 'top';
    });

    // Hide characters initially
    gsap.set(this.split.chars, { yPercent: 110, opacity: 0 });

    // ScrollTrigger reveal
    this.timeline = gsap.timeline({
      scrollTrigger: {
        trigger: this.el,
        start: 'top 85%',
        toggleActions: 'play none none none'
      }
    });

    this.timeline.to(this.split.chars, {
      opacity: 1,
      yPercent: 0,
      stagger: 0.03,
      duration: 1.0,
      ease: 'power4.out'
    });
  }

  destroy() {
    if (this.timeline && this.timeline.scrollTrigger) {
      this.timeline.scrollTrigger.kill();
    }
    if (this.split) {
      this.split.revert();
    }
  }
}

// ─── 4. INTERACTIVE 3D PERSPECTIVE TILT ──────────────────────────────────────
class Hover3DTilt {
  constructor(element) {
    this.el = element;
    this.el.style.transformStyle = 'preserve-3d';
    this.bindEvents();
  }

  bindEvents() {
    this.onMouseMove = (e) => {
      const rect = this.el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      gsap.to(this.el, {
        rotateY: x * 0.08,
        rotateX: -y * 0.08,
        z: 8,
        transformPerspective: 500,
        ease: 'power2.out',
        duration: 0.3
      });
    };

    this.onMouseLeave = () => {
      gsap.to(this.el, {
        rotateY: 0,
        rotateX: 0,
        z: 0,
        ease: 'power3.out',
        duration: 0.5
      });
    };

    this.el.addEventListener('mousemove', this.onMouseMove);
    this.el.addEventListener('mouseleave', this.onMouseLeave);
  }

  destroy() {
    this.el.removeEventListener('mousemove', this.onMouseMove);
    this.el.removeEventListener('mouseleave', this.onMouseLeave);
    gsap.killTweensOf(this.el);
  }
}

// ─── 5. CARDS GRID REVEAL MODULE ───────────────────────────────────────────
class CardsGridReveal {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.cards = wrapper.querySelectorAll('.why-soreze-stack-card');
    this.ctx = null;
    this.init();
  }

  init() {
    if (this.cards.length === 0) return;

    this.ctx = gsap.context(() => {
      gsap.fromTo(this.cards,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.12,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: this.wrapper,
            start: 'top 85%'
          }
        }
      );
    });
  }

  destroy() {
    if (this.ctx) {
      this.ctx.revert();
    }
  }
}

// ─── 6. DYNAMIC NUMBER COUNT-UP (STATS MODULE) ──────────────────────────────
class StatCounter {
  constructor(element) {
    this.el = element;
    this.originalText = this.el.textContent.trim();
    // Parse target number
    const match = this.originalText.match(/([0-9.]+)/);
    this.targetValue = match ? parseFloat(match[1]) : 0;

    // Parse prefixes and suffixes
    const splitParts = this.originalText.split(match ? match[1] : '');
    this.prefix = splitParts[0] || '';
    this.suffix = splitParts[1] || '';

    this.init();
  }

  init() {
    if (this.targetValue === 0) return;

    const countObj = { val: 0 };
    this.tween = gsap.to(countObj, {
      val: this.targetValue,
      duration: 1.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: this.el,
        start: 'top 92%',
        toggleActions: 'play none none none'
      },
      onUpdate: () => {
        // Handle decimals if any exist in the original number format
        const formattedVal = this.targetValue % 1 === 0
          ? Math.round(countObj.val)
          : countObj.val.toFixed(1);
        this.el.textContent = `${this.prefix}${formattedVal}${this.suffix}`;
      }
    });
  }

  destroy() {
    if (this.tween) {
      if (this.tween.scrollTrigger) this.tween.scrollTrigger.kill();
      this.tween.kill();
    }
    this.el.textContent = this.originalText;
  }
}

// ─── 7. ENDLESS HORIZONTAL LOGO MARQUEE MODULE ───────────────────────────────
class InfiniteMarquee {
  constructor(container) {
    this.container = container;
    this.track = null;
    this.tween = null;
    this.init();
  }

  init() {
    const items = this.container.children;
    if (items.length === 0) return;

    // Create wrapper track
    this.track = document.createElement('div');
    this.track.className = 'marquee-track flex gap-8 items-center w-max';

    // Move elements inside track
    const elements = Array.from(items);
    elements.forEach(el => this.track.appendChild(el));

    // Duplicate twice for infinite loop sizing
    const content = this.track.innerHTML;
    this.track.innerHTML = content + content + content;
    this.container.appendChild(this.track);
    this.container.style.overflow = 'hidden';

    // Animate the track infinitely
    this.tween = gsap.to(this.track, {
      xPercent: -33.33,
      ease: 'none',
      duration: 16,
      repeat: -1
    });
  }

  destroy() {
    if (this.tween) this.tween.kill();
    if (this.track) {
      // Revert children
      const originalChildren = Array.from(this.track.children).slice(0, this.track.children.length / 3);
      this.container.innerHTML = '';
      originalChildren.forEach(child => this.container.appendChild(child));
    }
  }
}

// ─── 8. CUSTOM LAG-BEHIND CURSOR MODULE ──────────────────────────────────────
class CustomCursor {
  constructor() {
    this.cursor = null;
    this.follower = null;
    // Custom circle cursor disabled to restore standard browser mouse pointer
  }

  init() {
    return;
  }

  destroy() {
    return;
  }
}


// ─── 9. GENERAL SCROLL TRIGGER ANIMATION CONTROLLER ──────────────────────────
class SectionAnimationSystem {
  constructor() {
    this.instances = new Map();
    this.lenisScroller = null;
    this.cursor = null;
    this.globalCtx = null;
  }

  initGlobals() {
    this.lenisScroller = new LenisScroll();
    this.cursor = new CustomCursor();
    this.globalCtx = gsap.context(() => {
      // Global magnetic button instantiations
      const magnets = document.querySelectorAll('.site-wrapper a[class*="bg-teal"], .site-wrapper a[class*="border-teal"], .site-wrapper button, .pulse-button, #shopify-section-hero a');
      magnets.forEach(el => new MagneticElement(el));

      // Global clip-path reveal animations for images
      document.querySelectorAll('#shopify-section-hero img, #shopify-section-founder_story img').forEach(img => {
        gsap.fromTo(img,
          { clipPath: 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)', scale: 1.1 },
          { clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)', scale: 1.0, duration: 1.6, ease: 'power4.inOut' }
        );
      });
    });

    // Refresh ScrollTrigger when images load to prevent layout shifts
    window.addEventListener('load', () => ScrollTrigger.refresh());
    document.querySelectorAll('img').forEach(img => {
      if (img.complete) return;
      img.addEventListener('load', () => ScrollTrigger.refresh());
    });
  }

  initSection(sectionElement) {
    const sectionId = sectionElement.id || sectionElement.className;
    if (this.instances.has(sectionId)) {
      this.destroySection(sectionId);
    }

    const sectionInstances = [];
    const ctx = gsap.context(() => {
      // A. Text reveals
      sectionElement.querySelectorAll('.gsap-section-header h2, #shopify-section-hero h1').forEach(text => {
        sectionInstances.push(new SplitTextReveal(text));
      });

      // B. 3D Card Tilts
      sectionElement.querySelectorAll('#shop-by-condition .grid > div, #shopify-section-featured_products .grid > article').forEach(card => {
        sectionInstances.push(new Hover3DTilt(card));
      });

      // C. Why Soreze Cards
      const stackWrapper = sectionElement.querySelector('.why-soreze-pin-wrapper');
      if (stackWrapper) {
        sectionInstances.push(new CardsGridReveal(stackWrapper));
      }

      // D. Stat Counters
      sectionElement.querySelectorAll('.why-soreze-stack-card p.font-display, #shopify-section-results-outcomes p.text-3xl, #shopify-section-hero a.glass-card p').forEach(stat => {
        // Simple regex check if contains numbers to verify it's a stat
        if (/[0-9]/.test(stat.textContent)) {
          sectionInstances.push(new StatCounter(stat));
        }
      });

      // E. Media Outlets Infinite Marquee
      const marqueeContainer = sectionElement.querySelector('#shopify-section-featured_stats div.mt-2\\.5, #shopify-section-featured-stats div.mt-2\\.5');
      if (marqueeContainer) {
        sectionInstances.push(new InfiniteMarquee(marqueeContainer));
      }

      // F. Stagger Reveals
      const gridReveal = sectionElement.querySelector('#shopify-section-featured_products .grid, #shopify-section-testimonials .grid');
      if (gridReveal && typeof ScrollTrigger !== 'undefined') {
        gsap.fromTo(gridReveal.children,
          { opacity: 0, y: 35 },
          {
            opacity: 1,
            y: 0,
            stagger: 0.12,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: gridReveal,
              start: 'top 85%'
            }
          }
        );
      }
    }, sectionElement);

    sectionInstances.push({ destroy: () => ctx.revert() });
    this.instances.set(sectionId, sectionInstances);
  }

  destroySection(sectionId) {
    const sectionInstances = this.instances.get(sectionId);
    if (sectionInstances) {
      sectionInstances.forEach(instance => {
        if (typeof instance.destroy === 'function') {
          instance.destroy();
        }
      });
      this.instances.delete(sectionId);
    }
  }

  destroyAll() {
    this.instances.forEach((_, id) => this.destroySection(id));
    if (this.globalCtx) this.globalCtx.revert();
    if (this.lenisScroller) this.lenisScroller.destroy();
    if (this.cursor) this.cursor.destroy();
  }
}

// ─── 10. SHOPIFY THEME EDITOR SYNC & BOOTSTRAP ───────────────────────────────
const animationSystem = new SectionAnimationSystem();

const initAllAnimations = () => {
  animationSystem.initGlobals();
  document.querySelectorAll('section, .site-wrapper').forEach(section => {
    animationSystem.initSection(section);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllAnimations);
} else {
  initAllAnimations();
}

// Listen to Shopify Theme Editor Section Lifecycle events
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target;
  animationSystem.initSection(section);
  ScrollTrigger.refresh();
});

document.addEventListener('shopify:section:unload', (e) => {
  const sectionId = e.target.id || e.target.className;
  animationSystem.destroySection(sectionId);
  ScrollTrigger.refresh();
});
